import { useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  CONTACT_DUPLICATES,
  CONTACT_FIELDS,
  CREATE_PLAYER_CONTACT,
  GET_PLAYER_CONTACT_REACH,
  REMOVE_PLAYER_CONTACT,
  UPDATE_PLAYER_CONTACT
} from '../queries/player-contact.queries';
import { createCacheModifier, removeCacheModifier } from '../cache/basic.cache';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useLogQueryError } from '../hooks/use-log-query-error';
import { COMMUNICATION_LIST_QUERIES } from './email-log.service';
import { REPORT_CARD_ROW_QUERIES } from './report-card-access.service';
import { ContactRelationship, IContactDuplicateMatch, IPlayerContactCandidate, SportInvolvement } from '../types';

const EMPTY_MATCHES: IContactDuplicateMatch[] = [];

// `contactReach` is a server projection over the player's contacts, so every contact write refetches it.
export const CONTACT_REACH_QUERIES = ['GetPlayerContactReach'];

// HOOK: whether anyone on file for this player can be emailed or texted.
export const useContactReach = (playerAssessmentId?: string): ContactReach | null | undefined => {
  const { data, error } = useQuery(GET_PLAYER_CONTACT_REACH, {
    skip: !playerAssessmentId,
    variables: { _id: playerAssessmentId },
    fetchPolicy: 'cache-and-network',
  });
  useLogQueryError(error, 'playerContactReach');

  return data?.playerAssessment?.contactReach;
};

// HOOK: which of the entered contact's fields already appear on `existing`.
// Advisory and debounced; the mutations enforce their own duplicate checks.
export const useContactDuplicates = (
  candidate: IPlayerContactCandidate,
  existing: IPlayerContactCandidate[],
): IContactDuplicateMatch[] => {
  const { name, email, phone } = candidate;
  // Each scalar debounces on its own: passing the candidate object would restart
  // the timer every render, since it is a fresh identity each time.
  const debouncedName = useDebouncedValue(name || '');
  const debouncedEmail = useDebouncedValue(email || '');
  const debouncedPhone = useDebouncedValue(phone || '');

  const hasCandidate = !!(debouncedName || debouncedEmail || debouncedPhone);
  const { data, error } = useQuery(CONTACT_DUPLICATES, {
    skip: !hasCandidate || existing.length === 0,
    variables: {
      candidate: { name: debouncedName, email: debouncedEmail, phone: debouncedPhone },
      existing: existing.map((row) => ({ name: row.name, email: row.email, phone: row.phone })),
    },
  });
  useLogQueryError(error, 'contactDuplicates');

  return useMemo(() => data?.contactDuplicates || EMPTY_MATCHES, [data]);
};

export interface ICreatePlayerContactProps {
  name: string;
  email?: string;
  phone?: string;
  playerId?: string;
  sendSMS?: boolean;
  sendEmail?: boolean;
  height?: number;
  sportInvolvement?: SportInvolvement;
  relationship?: ContactRelationship;
}

export const buildCreatePlayerContact = ({playerAssessmentId}: {playerAssessmentId?: string}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(CREATE_PLAYER_CONTACT);

  return {
    ...rest,
    createPlayerContact: (data: ICreatePlayerContactProps) => mutation({
      variables: { data, playerAssessmentId },
      // Re-adding an address restores Resend on rows that went to it — same
      // server projection as the remove/update paths below.
      refetchQueries: [...COMMUNICATION_LIST_QUERIES, ...CONTACT_REACH_QUERIES],
      update: (cache, { data: responseData }) => {
        const result = responseData && responseData.createPlayerContact;
        const createdDoc = result?.contact;

        // Only add to cache if it was a new contact (not updated) and contact is not null
        if (createdDoc && !result.wasUpdated && result.status === 'created') {
          cache.modify({
            id: `Player:${data.playerId}`,
            fields: {
              contacts: createCacheModifier({
                cache,
                createdDoc,
                modelName: 'PlayerContact',
                fragment: CONTACT_FIELDS,
                fragmentName: 'ContactFields'
              })
            }
          });
        }
      },
      optimisticResponse: {
        __typename: 'Mutation',
        createPlayerContact: {
          __typename: 'PlayerContactResponse',
          contact: {
            __typename: 'PlayerContact',
            ...data
          },
          wasUpdated: false
        }
      }
    })
  }
};

export const buildRemovePlayerContact = ({playerAssessmentId}: {playerAssessmentId?: string}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(REMOVE_PLAYER_CONTACT);

  return {
    ...rest,
    removePlayerContact: (_id: string) => mutation({
      variables: { _id, playerAssessmentId },
      // `hasActiveRecipient` and contact reach are server projections over this player's contacts, so the
      // communications panel's Resend, the report card's contact links and the reach warnings refetch.
      refetchQueries: [...COMMUNICATION_LIST_QUERIES, ...REPORT_CARD_ROW_QUERIES, ...CONTACT_REACH_QUERIES],
      update: cache => {
        cache.modify({
          fields: {
            contacts: removeCacheModifier(cache, _id)
          }
        })
      },
      optimisticResponse: {
        __typename: 'Mutation',
        removePlayerContact: {
          __typename: 'DeleteResponse',
          _id
        }
      }
    })
  };
};

export interface IUpdatePlayerContactProps {
  name?: string;
  phone?: string;
  email?: string;
  height?: number;
  sportInvolvement?: SportInvolvement;
  relationship?: ContactRelationship;
}

export const buildUpdatePlayerContact = ({playerAssessmentId}: {playerAssessmentId?: string}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(UPDATE_PLAYER_CONTACT);

  return {
    ...rest,
    updatePlayerContact: (_id: string, data: IUpdatePlayerContactProps) => mutation({
      variables: { _id, data, playerAssessmentId },
      // `hasActiveRecipient` is a server projection over this player's contacts,
      // so the Resend buttons in the communications panel go stale on this write.
      refetchQueries: [...COMMUNICATION_LIST_QUERIES, ...CONTACT_REACH_QUERIES],
      optimisticResponse: {
        __typename: 'Mutation',
        updatePlayerContact: {
          __typename: 'PlayerContact',
          _id,
          ...data
        }
      }
    })
  }
}
